import React from 'react';
import { useFormContext } from 'react-hook-form';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import ControlledSelect from '@/components/common/form/ControlledSelect';
import ControlledStartEndDateInputs from '@/components/common/form/ControlledStartEndDateInputs';
import ControlledTextField from '@/components/common/form/ControlledTextField';
import CustomFieldTypes from '@/constants/customFields/CustomFieldTypes';
import translate from '@/lib/intl/translate';
import type CustomField from '@/types/api/CustomField';

type OptionValue = string | boolean | null | undefined;
type InputOption = { label: string; value: OptionValue };

function CustomFieldInput({
  customFieldId,
  fieldName,
  fieldOptions,
  fieldType,
  slug,
}: CustomField) {
  const { control, formState, trigger, watch } = useFormContext();
  const fieldValue = watch(slug);

  let options: InputOption[] = [];
  let multiple = false;

  const getFieldLabel = (val: OptionValue) =>
    options.find((item) => item.value === val)?.label;

  switch (fieldType) {
    case CustomFieldTypes.CHECKBOX:
    case CustomFieldTypes.LIST_MULTI:
    case CustomFieldTypes.LIST_SINGLE:
      options =
        fieldType === CustomFieldTypes.CHECKBOX
          ? [
              { label: translate('TRUE'), value: true },
              { label: translate('FALSE'), value: false },
            ]
          : (fieldOptions || []).map((label) => ({ label, value: label }));
      multiple = fieldType === CustomFieldTypes.LIST_MULTI;
      return (
        <FormControl className="w-full" size="small">
          <InputLabel
            id={`${customFieldId}-label`}
            shrink={
              fieldValue === false ||
              !!(Array.isArray(fieldValue) ? fieldValue.length : fieldValue)
            }
          >
            {fieldName}
          </InputLabel>
          <ControlledSelect
            defaultValue={multiple ? [] : ''}
            labelId={`${customFieldId}-label`}
            control={control}
            name={slug}
            label={fieldName}
            multiple={multiple}
            renderValue={(selected: unknown) =>
              Array.isArray(selected) ? (
                <div className="max-w-full truncate">
                  {selected.map(getFieldLabel).join(', ')}
                </div>
              ) : (
                getFieldLabel(selected as OptionValue)
              )
            }
          >
            {!multiple && (
              <MenuItem className="text-text-secondary" value="">
                {translate('ANY')}
              </MenuItem>
            )}
            {options.map(({ label, value }) => (
              <MenuItem
                key={label}
                value={value as string}
                className="flex items-center"
              >
                {label}
                {multiple && (
                  <Checkbox
                    className="ml-auto"
                    checked={fieldValue?.includes(value)}
                  />
                )}
              </MenuItem>
            ))}
          </ControlledSelect>
        </FormControl>
      );
    case CustomFieldTypes.TEXT_MULTI:
    case CustomFieldTypes.TEXT_SINGLE:
      return (
        <ControlledTextField
          control={control}
          defaultValue=""
          id={`${customFieldId}-input`}
          InputLabelProps={{
            htmlFor: `${customFieldId}-input`,
          }}
          label={fieldName}
          name={slug}
        />
      );
    case CustomFieldTypes.DATE_PICKER:
      return (
        <div className="grid grid-cols-2 gap-x-4 gap-y-2">
          <div className="col-span-full type-caption2">{fieldName}</div>
          <ControlledStartEndDateInputs
            control={control}
            endDateName={`${slug}.1`}
            errors={formState.errors}
            id="constituent-filter-date-picker"
            startDateName={`${slug}.0`}
            trigger={trigger}
            watch={watch}
          />
        </div>
      );
    default:
      return null;
  }
}

export default CustomFieldInput;
